import React, { useState } from 'react';
import classNames from 'classnames';
import getAlpha2Code from '#modules/utils/getAlpha2Code';
import { useSupportedCurrencyCodes } from '#modules/api/Banking';
import SearchableDropdown from '#components/Elements/SearchableDropdown/SearchableDropdown';
import { BankingContext } from '#modules/context/BankingContext';
import styles from './DisplayCurrencyPicker.scss';

// Currency to country mapping for flag display
const CURRENCY_TO_COUNTRY = {
  AUD: 'AUS',
  CAD: 'CAN',
  EUR: 'EU', // Use EU instead of EUR for flag
  GBP: 'GBR',
  NZD: 'NZL',
  SGD: 'SGP',
  USD: 'USA',
  TRY: 'TUR',
  CNY: 'CHN',
  INR: 'IND',
  VND: 'VNM',
  IDR: 'IDN',
  ILS: 'ISR',
};

// Currency display names
const CURRENCY_NAMES = {
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  NZD: 'New Zealand Dollar',
  SGD: 'Singapore Dollar',
  USD: 'US Dollar',
  TRY: 'Turkish Lira',
  CNY: 'Chinese Yuan',
  INR: 'Indian Rupee',
  VND: 'Vietnamese Dong',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli Shekel',
};

// Currency symbols
export const CURRENCY_SYMBOLS = {
  AUD: 'A$',
  CAD: 'C$',
  EUR: '€',
  GBP: '£',
  NZD: 'NZ$',
  SGD: 'S$',
  USD: '$',
  TRY: '₺',
  CNY: '¥',
  INR: '₹',
  VND: '₫',
  IDR: 'Rp',
  ILS: '₪',
};

function getCurrencyCountryCode(currencyCode) {
  return CURRENCY_TO_COUNTRY[currencyCode];
}

export default function DisplayCurrencyPicker({ className, compact = false }) {
  const [search, setSearch] = useState('');
  const { displayCurrencyCode, setDisplayCurrencyCode } =
    React.useContext(BankingContext);
  const supportedCurrencyCodes = useSupportedCurrencyCodes();
  const items = supportedCurrencyCodes
    ?.map((code) => ({
      code,
      name: CURRENCY_NAMES[code],
      symbol: CURRENCY_SYMBOLS[code],
    }))
    .filter(
      ({ name, code }) =>
        name.toLowerCase().includes(search.toLowerCase()) ||
        code.toLowerCase().includes(search.toLowerCase()),
    );

  const selectedCurrency = {
    code: displayCurrencyCode,
    name: CURRENCY_NAMES[displayCurrencyCode],
    symbol: CURRENCY_SYMBOLS[displayCurrencyCode],
  };

  return (
    <SearchableDropdown
      dropdownRelative
      className={classNames(
        styles.currencyDropdown,
        { [styles.compact]: compact },
        className,
      )}
      onChange={({ code }) => setDisplayCurrencyCode(code)}
      trigger={
        <div className={styles.trigger}>
          <span
            className={classNames(
              styles.flag,
              displayCurrencyCode &&
                `flag-${getAlpha2Code(getCurrencyCountryCode(displayCurrencyCode))}`,
            )}
          />
          {!compact && (
            <span className={styles.currencyName}>{selectedCurrency.code}</span>
          )}
        </div>
      }
      items={items}
      search={search}
      setSearch={setSearch}
      render={({ code, name, symbol }, active) => (
        <div
          key={code}
          className={classNames(styles.optionRow, active && styles.active)}
        >
          <div
            className={classNames(
              styles.flag,
              code && `flag-${getAlpha2Code(getCurrencyCountryCode(code))}`,
            )}
          />
          <div className={styles.currencyInfo}>
            <div className={styles.symbol}>{symbol}</div>
            <div className={styles.name}>{name}</div>
          </div>
          <div className={styles.code}>{code}</div>
        </div>
      )}
    />
  );
}
